import { color } from 'asserts/styles/colors';
import { font } from 'asserts/styles/fonts';
import styled from 'styled-components';
import { Button, ConfigProvider } from 'antd';
import { PoweroffOutlined } from '@ant-design/icons';

interface DefaultButtonProps {
  text: string;
  onClick?: () => void;
  loading?: boolean;
  size?: 'large' | 'middle' | 'small';
  shape?: 'default' | 'circle' | 'round';
  type?: 'primary' | 'dashed' | 'link' | 'text' | 'default';
  style?: React.CSSProperties;
  disabled?: boolean;
  rightIcon?: React.ReactNode;
}

const DefaultButton = ({
  text,
  onClick,
  loading = false,
  size = 'middle',
  shape = 'round',
  type = 'default',
  style,
  disabled = false,
  rightIcon
}: DefaultButtonProps) => {
  return (
    <ButtonWrapper $type={type}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#B6B6B6',
            borderRadius: 2
          }
        }}
      >
        {type !== 'link' ? (
          <Button
            size={size}
            shape={shape}
            onClick={onClick}
            loading={loading}
            icon={loading ? <PoweroffOutlined /> : null}
            type={type}
            style={style}
            disabled={disabled}
          >
            {loading ? '' : text}
            {rightIcon && <>{rightIcon}</>}
          </Button>
        ) : (
          <Button
            size={size}
            shape={shape}
            onClick={onClick}
            loading={loading}
            icon={loading ? <PoweroffOutlined /> : null}
            type={type}
            style={style}
            disabled={disabled}
          >
            {loading ? '' : text}
            {rightIcon && <>{rightIcon}</>}
          </Button>
        )}
      </ConfigProvider>
    </ButtonWrapper>
  );
};

export default DefaultButton;

const ButtonWrapper = styled.p<{ $type: string }>`
  min-width: ${(props) => props.$type === 'text' && '160px'};

  .ant-btn.ant-btn-text {
    &.ant-btn.ant-btn-round {
      border-radius: 4px;
      padding: 4px 8px;
    }
  }

  .ant-btn:not(.ant-btn-text) {
    padding: 0 21px;
    display: flex;
    justify-content: center;
    align-items: center;
    ${font.body_medium};
    height: 44px;
    gap: 8px;

    span {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    //size
    &.ant-btn-lg {
      padding: 0 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      ${font.h3_medium};
      height: 48x;
    }
    &.ant-btn-sm {
      // padding: 0 15px;
      display: flex;
      justify-content: center;
      align-items: center;
      ${font.caption2_medium};
      height: 32px;
      svg {
        width: 16px;
        height: 16px;
      }
    }

    //color
    &.ant-btn-default {
      color: ${color.gray400};
      border-color: ${color.gray400};
      background-color: rgba(0, 0, 0, 0);
      &:hover {
        background-color: ${color.opacityBlack01};
        box-shadow: 0px 0px 0px 4px ${color.opacityBlack01};
      }
    }

    &.ant-btn-primary {
      background-color: ${color.black2};
      color: ${color.white};

      &:hover {
        background-color: ${color.black2};
        color: ${color.white};
        box-shadow: 0px 0px 0px 4px ${color.opacityBlack01};
      }
    }

    &.ant-btn-link {
      background-color: ${color.gray500};

      &:hover {
        color: ${color.white};
        box-shadow: 0px 0px 0px 4px ${color.opacityBlack01};
      }
    }

    &:disabled {
      opacity: 0.2;
    }
  }
  .ant-btn:focus,
  .ant-btn:focus-within {
    box-shadow: 0px 0px 0px 4px ${color.opacityBlack01};
    outline: none;
  }
`;
